import { Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { createCheckoutSession } from "@/lib/stripe.functions";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";

type UpgradeModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function UpgradeModal({ open, onOpenChange }: UpgradeModalProps) {
  const { session } = useAuth();
  const checkout = useServerFn(createCheckoutSession);

  async function onUpgrade() {
    try {
      const headers = session?.access_token
        ? { Authorization: `Bearer ${session.access_token}` }
        : undefined;
      const { url } = await checkout({
        data: { origin: window.location.origin },
        headers,
      });
      window.location.href = url;
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="upgrade-modal">
        <DialogHeader>
          <DialogTitle>You&apos;ve used all 3 free generations</DialogTitle>
          <DialogDescription>
            Upgrade to Pro for unlimited proposals, saved documents, and early access to
            E-Signature.
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-1.5 text-sm">
          <li>✓ Unlimited proposals and contracts</li>
          <li>✓ Full saved document dashboard</li>
          <li>✓ E-Signature early access (coming soon)</li>
        </ul>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Maybe later
          </Button>
          {session ? (
            <Button onClick={onUpgrade}>Upgrade to Pro — $19/month</Button>
          ) : (
            <Button asChild>
              <Link to="/signup">Upgrade to Pro — $19/month</Link>
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
